import {
  RequiredEffectivePeriodSchema,
  DaysOfTheWeekSchema,
  EffectiveTimeRangesSchema,
  TimeZoneSchema,
  DateRecordSchema,
  DateTimeRecordSchema,
  RotatingPatternSchema,
} from "@visioni/schedules";
import { UserIdSchema } from "@visioni/a3s-users";
import { AlarmSchedule } from "./alarm-schedules/types";
import { z } from "zod";

export const Author = z.union([
  z.object({ kind: z.literal("user"), id: UserIdSchema }),
  z.object({ kind: z.literal("system") }),
]);
export type Author = z.infer<typeof Author>;

// TODO: These contact schemas belong in a3s-schema as domain primitives. They live here
// only because AlarmConfiguration.settings.contacts bundles them in the same database row.
// Consumed by a3s-alarm-activations, a3s-provisioning, and 18+ files in a3s-app.
export const PhoneNumber = z
  .string()
  .regex(/^\+\d{6,15}$/, "Phone number must be in international format, e.g. +61123456789");
export type PhoneNumber = z.infer<typeof PhoneNumber>;

export const SmsContact = z.object({
  id: z.string().optional(),
  phone_number: PhoneNumber,
  name: z.string(),
});
export type SmsContact = z.infer<typeof SmsContact>;

export const EmailContact = z.object({
  id: z.string().optional(),
  email: z.email(),
  name: z.string().optional(),
});
export type EmailContact = z.infer<typeof EmailContact>;

/**
 * Per-camera contact caps.
 *
 * Exported because they are load-bearing beyond parsing: a stored list over the
 * cap fails {@link AlarmContacts} at alarm dispatch time and the camera loses
 * ALL of its notifications. Anything that appends a contact has to check the cap
 * itself before writing, and must check against this value rather than its own
 * copy of the number.
 */
export const MAX_ALARM_SMS_CONTACTS = 5;
export const MAX_ALARM_EMAIL_CONTACTS = 5;

/**
 * Contacts that receive notifications when an alarm is triggered.
 */
export const AlarmContacts = z.object({
  sms_contacts: z.array(SmsContact).max(MAX_ALARM_SMS_CONTACTS).default([]),
  email_contacts: z.array(EmailContact).max(MAX_ALARM_EMAIL_CONTACTS).default([]),
});
export type AlarmContacts = z.infer<typeof AlarmContacts>;

/**
 * Suggested emails to add as a contact against a camera.
 */
export const EmailSuggestion = z.object({
  email: z.email(),
});
export type EmailSuggestion = z.infer<typeof EmailSuggestion>;

export const EmailSuggestionListResponse = z.array(EmailSuggestion);
export type EmailSuggestionListResponse = z.infer<typeof EmailSuggestionListResponse>;

/**
 * Domain model for ALARM configuration used by the UX and services.
 *
 * The ALARM model is intentionally opinionated and maps cleanly to the generic
 * Schedule model from @visioni/schedules:
 * - Alarm.response         -> Rule.features (states to apply during "armed" windows)
 * - Alarm.routine          -> Repeating windows (typically weekly or rotating)
 * - Alarm.exceptions       -> Absolute overrides (date/time specific) that set the
 *   alarm action to either "armed" or "disarmed" within their period.
 * - Alarm.scheduleState    -> Schedule state (configured vs applied)
 *
 * Time semantics:
 * - All date/time records are interpreted in the Alarm.timezone unless stated otherwise.
 * - EffectiveTimeRanges represent local time-of-day ranges, including [all_day].
 */

/**
 * Basic enabled/disabled state for an actuator.
 * Use "on" | "off" to align with common device semantics and keep mapping simple.
 */
export const AlarmResponseToggle = z.enum(["on", "off"]);
export type AlarmResponseToggle = z.infer<typeof AlarmResponseToggle>;

/**
 * Declarative response describing how the alarm behaves when "armed".
 */
export const AlarmResponse = z.object({
  recording: AlarmResponseToggle,
  monitoring: AlarmResponseToggle,
  sirens: AlarmResponseToggle,
  strobes: AlarmResponseToggle,
});
export type AlarmResponse = z.infer<typeof AlarmResponse>;

/**
 * Canonical set of actuators/features the ALARM can control at a UX level.
 * These are mapped to concrete schedule features via service-specific adaptors.
 */
export type AlarmFeatures = keyof typeof AlarmResponse.shape;

/**
 * Weekly routine window: days-of-week and local time ranges, optionally bounded
 * by an effective period (e.g. valid for next 6 months).
 */
export const WeeklyRoutineWindow = z.object({
  // User-facing label for this window (e.g. "Weeknights", "Weekend").
  name: z.string().optional(),
  // One or more days of the week this window applies to.
  days: DaysOfTheWeekSchema,
  // One or more local time-of-day ranges that apply on the specified days.
  timeRanges: EffectiveTimeRangesSchema,
});
export type WeeklyRoutineWindow = z.infer<typeof WeeklyRoutineWindow>;

/**
 * Weekly-based routine (most common): a set of weekly windows that indicate when
 * the alarm is "active" (i.e. apply response.active).
 */
export const WeeklyAlarmRoutine = z.object({
  kind: z.literal("weekly"),
  windows: z.array(WeeklyRoutineWindow),
});
export type WeeklyAlarmRoutine = z.infer<typeof WeeklyAlarmRoutine>;

/**
 * Rotating routine for shift-style configurations. Each interval spans a number
 * of days and either specifies time ranges (active) or an explicit off day.
 * The pattern repeats indefinitely starting from `start`.
 */
export const RotatingAlarmRoutine = z.object({
  kind: z.literal("rotating"),
  // Local start date (no time) for the first day of the rotation
  start: DateRecordSchema,
  // Sequence of intervals that comprise the rotation
  pattern: z.array(RotatingPatternSchema),
});
export type RotatingAlarmRoutine = z.infer<typeof RotatingAlarmRoutine>;

/**
 * The timezone considered by the user when changeing alarm settings.
 */
export const AlarmTimezone = TimeZoneSchema;
export type AlarmTimezone = z.infer<typeof TimeZoneSchema>;

/**
 * Core repeating routine (weekly or rotating) that determines when the alarm
 * is considered "active" vs "inactive".
 */
export const AlarmRoutine = z.discriminatedUnion("kind", [WeeklyAlarmRoutine, RotatingAlarmRoutine]);
export type AlarmRoutine = z.infer<typeof AlarmRoutine>;

/**
 * Absolute, date/time-specific exception behavior applied over the baseline routine.
 * Exceptions are evaluated in the alarm's timezone and take precedence during their period.
 */
export const AlarmAction = z.enum(["armed", "disarmed"]);
export type AlarmAction = z.infer<typeof AlarmAction>;

export const AlarmException = z.object({
  id: z.string(),
  // Reason label for UX (e.g. "Xmas Day", "Public Holiday").
  reason: z.string(),
  // The action to apply during the exception period.
  action: AlarmAction,
  // Absolute localised period when the exception is in effect.
  period: RequiredEffectivePeriodSchema,
  author: Author.nullable().default(null),
});
export type AlarmException = z.infer<typeof AlarmException>;

export const AlarmExceptions = z.array(AlarmException);
export type AlarmExceptions = z.infer<typeof AlarmExceptions>;

/**
 * Suggested exception derived from holidays or other calendar events.
 */
export const AlarmExceptionSuggestion = z.object({
  id: z.string(),
  reason: z.string(),
  action: AlarmAction,
  date: DateRecordSchema,
  isAllDay: z.boolean(),
});
export type AlarmExceptionSuggestion = z.infer<typeof AlarmExceptionSuggestion>;

export const AlarmExceptionSuggestionsResponse = z.array(AlarmExceptionSuggestion);
export type AlarmExceptionSuggestionsResponse = z.infer<typeof AlarmExceptionSuggestionsResponse>;

/**
 * A single override that applies as the final rule in the schedule stack.
 * Similar to an exception, but with nullable end to support indefinite overrides.
 */
export const AlarmOverridePeriodSchema = z.object({
  start: DateTimeRecordSchema,
  end: DateTimeRecordSchema.nullable(),
});
export type AlarmOverridePeriod = z.infer<typeof AlarmOverridePeriodSchema>;

const AlarmOverrideSchema = z.object({
  id: z.string(),
  // Reason label for UX (e.g. "Disarm for 15 mins", "Disarm until I arm again").
  reason: z.string(),
  // The action to apply during the override period.
  action: AlarmAction,
  // Absolute localised period when the override is in effect.
  // end can be null for indefinite overrides (e.g. "Disarm until I arm again").
  period: AlarmOverridePeriodSchema,
  author: Author.nullable().default(null),
});
export const AlarmOverride = AlarmOverrideSchema;
export type AlarmOverride = z.infer<typeof AlarmOverrideSchema>;

/**
 * A silence forces sirens and strobes off for the duration of the period.
 */
export const AlarmSilencePeriodSchema = z.object({
  start: DateTimeRecordSchema,
  end: DateTimeRecordSchema,
});
export type AlarmSilencePeriod = z.infer<typeof AlarmSilencePeriodSchema>;

const AlarmSilenceSchema = z.object({
  id: z.string(),
  reason: z.string().optional(),
  period: AlarmSilencePeriodSchema,
  author: Author.nullable().default(null),
});
export const AlarmSilence = AlarmSilenceSchema;
export type AlarmSilence = z.infer<typeof AlarmSilenceSchema>;

export const AlarmConfigurationSchedule = z.object({
  schedule: AlarmSchedule,
  at: z.iso.datetime(),
});

/**
 * A summary of an alarm settings and schedules.
 */
export const AlarmConfiguration = z.object({
  settings: z
    .object({
      timezone: TimeZoneSchema.nullable(),
      response: AlarmResponse.nullable(),
      routine: AlarmRoutine.nullable(),
      exceptions: z.array(AlarmException),
      override: AlarmOverride.nullable(),
      silence: AlarmSilence.nullable(),
      contacts: AlarmContacts.nullable(),
      updatedAt: z.iso.datetime(),
    })
    .nullable(),
  configured: AlarmConfigurationSchedule.nullable(),
  applied: z.union([z.literal("configured"), AlarmConfigurationSchedule, z.null()]),
});
export type AlarmConfiguration = z.infer<typeof AlarmConfiguration>;

/** @deprecated Use AlarmConfiguration instead */
export const AlarmDetail = AlarmConfiguration;
/** @deprecated Use AlarmConfiguration instead */
export type AlarmDetail = AlarmConfiguration;

/**
 * A feature that can be controlled by the alarm.
 * Used by UX to display the available alarm features and their states.
 */
export const AlarmResponseFeature = z.object({
  defaultState: AlarmResponseToggle,
  allowedStates: z.array(AlarmResponseToggle),
  title: z.string().optional(),
  description: z.string().optional(),
});
export type AlarmResponseFeature = z.infer<typeof AlarmResponseFeature>;

export const AlarmResponseFeatures = {
  monitoring: {
    defaultState: "on",
    allowedStates: ["on"],
    title: "Monitoring",
    description:
      "Protect assets in real time and deploy response with Vision Intelligence 24-hour ASIAL-accredited monitoring service. Our highly trained in-house team know Vision Intelligence products inside and out, and are equipped to respond to events quickly and efficiently.",
  },
  sirens: {
    defaultState: "on",
    allowedStates: ["on", "off"],
    title: "Sirens",
  },
  strobes: {
    id: "strobes",
    defaultState: "on",
    allowedStates: ["on", "off"],
    title: "Strobes",
  },
};

export { computeScheduleForAlarmSettings } from "./schedule";
